import { GroupsIcon, HandshakeIcon, MedicalServicesIcon, VerifiedIcon } from '@uplift/icons';
import { CollapsableBulletList, SectionContainer, SubSectionContainer } from '../components';
import { useSelectedProviderContext } from '../selected-provider.context';
import { Box, Chip, Collapse, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSelectedProviderStyles } from '../styles';
import { SelectedProviderSectionProps, SelectedProviderSubSectionEnum, SelectedProviderTabEnum } from '../types';
import { ProviderMatchingProfileBookmark } from '@uplift/api';
import { useSelectedProviderFieldTransitionConfigs } from '../helpers';
import { ListItem } from '@uplift/types';

type CommunitiesSection = {
  title: string;
  listItems: ListItem[];
};

const sortByCommonalityOrder = <T extends { commonalityOrder?: number | null }>(a: T, b: T) => {
  return (a.commonalityOrder ?? 0) - (b.commonalityOrder ?? 0);
};

export type ExpertiseSectionProps = {
  selectedSpecialtyIds?: string[];
  selectedExperienceIds?: string[];
} & SelectedProviderSectionProps;

export const ExpertiseSection: React.VFC<ExpertiseSectionProps> = props => {
  const { classes } = useSelectedProviderStyles();
  const { classes: chipClasses } = useExpertiseChipStyles();
  const { selectedProvider } = useSelectedProviderContext();
  const { getTransitionConfig } = useSelectedProviderFieldTransitionConfigs(props.fieldTransitions);

  const communities: CommunitiesSection[] = [
    {
      title: 'Ethnicity and Race',
      listItems: selectedProvider.communityExpertise.ethnicity
    },
    {
      title: 'Faith',
      listItems: selectedProvider.communityExpertise.faith
    },
    {
      title: 'General',
      listItems: selectedProvider.communityExpertise.general
    },
  ];

  const selectedSpecialties = selectedProvider.specialties
    .filter(s => props.selectedSpecialtyIds?.includes(s.id))
    .sort(sortByCommonalityOrder)
    .map(s => <Typography className={classes.selectedSpecialty} key={s.id}>{s.name}</Typography>);

  const otherSpecialties = selectedProvider.specialties
    .filter(s => !props.selectedSpecialtyIds?.includes(s.id))
    .sort(sortByCommonalityOrder)
    .map(s => s.name);

  const supportedAgeGroupTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.supportedAgeGroups);
  const modalitiesTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.modalities);
  const specialtiesTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.specialties);
  const treatmentApproachTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.treatmentApproaches);
  const communityExpertiseTransitionConfig = getTransitionConfig(ProviderMatchingProfileBookmark.communityExpertise);


  return (
    <SectionContainer
      title="Expertise"
      id={SelectedProviderTabEnum.expertise}
      forceMobile={props.forceMobile}
    >
      <SubSectionContainer
        title="I work with these clients..."
        icon={HandshakeIcon}
        large
        id={SelectedProviderSubSectionEnum.workWithTheseClients}
        forceMobile={props.forceMobile}
        highlight={supportedAgeGroupTransitionConfig.active || modalitiesTransitionConfig.active}
        in={supportedAgeGroupTransitionConfig.shown || modalitiesTransitionConfig.shown}
      >
        <Collapse in={supportedAgeGroupTransitionConfig.transitionIn} timeout={500}>
          <Box display="flex" flexWrap="wrap" gap="0.5rem">
            {selectedProvider.supportedAgeGroups.map((ageGroup) => (
              <Chip
                key={ageGroup.id}
                label={ageGroup.name}
                variant="outlined"
                classes={chipClasses}
              />
            ))}
            {selectedProvider.modalities.map((modality, idx) => (
              <Collapse key={idx} in={modalitiesTransitionConfig.transitionIn} timeout={500}>
                <Chip
                  key={idx}
                  label={modality.name}
                  classes={chipClasses}
                />
              </Collapse>
            ))}
          </Box>
        </Collapse>
      </SubSectionContainer>
      <SubSectionContainer
        title="My specialties are..."
        icon={VerifiedIcon}
        large
        id={SelectedProviderSubSectionEnum.specialties}
        forceMobile={props.forceMobile}
        highlight={specialtiesTransitionConfig.active}
        in={specialtiesTransitionConfig.shown}
      >
        <Collapse in={specialtiesTransitionConfig.transitionIn} timeout={500}>
          <CollapsableBulletList
            listItems={[ ...selectedSpecialties, ...otherSpecialties ]}
            previewMaxItems={5}
          />
        </Collapse>
      </SubSectionContainer>
      <SubSectionContainer
        title="My treatment approaches are..."
        icon={MedicalServicesIcon}
        large
        id={SelectedProviderSubSectionEnum.treatmentApproach}
        forceMobile={props.forceMobile}
        highlight={treatmentApproachTransitionConfig.active}
        in={treatmentApproachTransitionConfig.shown}
      >
        <Collapse in={treatmentApproachTransitionConfig.transitionIn} timeout={500}>
          <CollapsableBulletList
            listItems={selectedProvider.treatmentApproach.sort(sortByCommonalityOrder).map(t => t.name)}
            previewMaxItems={5}
          />
        </Collapse>
      </SubSectionContainer>
      <SubSectionContainer
        title="I’ve worked with these communities"
        icon={GroupsIcon}
        large
        id={SelectedProviderSubSectionEnum.communityExpertise}
        forceMobile={props.forceMobile}
        hidden={!props.fieldTransitions && communities.every(c => !c.listItems.length)}
        highlight={communityExpertiseTransitionConfig.active}
        in={communityExpertiseTransitionConfig.shown}
      >
        <Collapse in={communityExpertiseTransitionConfig.transitionIn} timeout={500}>
          <Box display="flex" flexDirection="column" gap="0.5rem">
            {communities.map(({ title, listItems }, idx) => {

              if (!listItems.length) {
                return null;
              }

              return (
                <div key={idx}>
                  <Typography className={classes.subtitle}>{title}</Typography>
                  <CollapsableBulletList
                    listItems={listItems.map(({ name, id }) => {
                      const highlight = props.selectedExperienceIds?.includes(id);

                      if (highlight) {
                        return <Typography className={classes.selectedSpecialty} key={id}>{name}</Typography>;
                      }

                      return name;
                    })}
                    previewMaxItems={5}
                  />
                </div>
              );
            })}
          </Box>
        </Collapse>
      </SubSectionContainer>
    </SectionContainer>
  );
};

const useExpertiseChipStyles = makeStyles({ name: 'ExpertiseChip' })(theme => ({
  root: {
    fontSize: '16px',
    backgroundColor: theme.palette.info.background,
    height: '34px',
  },
  outlined: {
    backgroundColor: 'transparent',
    borderColor: theme.palette.info.main,
  }
}));
